/* eslint-disable */
import { useMutation, useQuery } from '@tanstack/react-query';
import type { QueryKey, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

import { studyAgentFetcher } from '../Fetcher';
import type { RequestOptions } from '../Fetcher';
const fetcher = studyAgentFetcher;

export type Maybe<T> = T | null;

export type OneOf<T> = T extends unknown[]
  ? T[number]
  : T extends readonly unknown[]
    ? T[number]
    : T;

export type Exact<T extends { [key: string]: unknown }> = {
  [K in keyof T]-?: Exclude<T[K], undefined>;
};

export type Values<T extends Record<string, unknown>> = T extends Record<string, infer U>
  ? U
  : T extends { [key: string]: infer U }
    ? U
    : never;

export const enum ResponseStatus {
  OK = 'OK',
  SERVER_ERROR = 'SERVER_ERROR',
  DUPLICATE = 'DUPLICATE',
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  BAD_PARAMETER = 'BAD_PARAMETER',
  CONFLICT = 'CONFLICT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

export type Success<T = unknown> = {
  statusCode: ResponseStatus.OK;
  message: '';
  data: T;
  errorCode: string;
};

export type Error = {
  statusCode: Omit<keyof typeof ResponseStatus, 'OK'>;
  message: string;
  data: Maybe<''>;
  errorCode: string;
};

/**
 * @typeParam T - Response data type
 */
export type ApiResponse<T = unknown> = Success<T>;

export const API_ENDPOINT = {
  CLIENT_V1_MOCK_INTERVIEW_VOUCHER_FREE: '/client/api/v1/mock-interview-voucher/free',
  CLIENT_V1_MOCK_INTERVIEW_VOUCHER: '/client/api/v1/mock-interview-voucher',
};

export function useClientApiV1MockInterviewVoucherFreeCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MockInterviewVoucherFreeCreateResponse,
    unknown,
    ClientApiV1MockInterviewVoucherFreeCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MockInterviewVoucherFreeCreateResponse,
    unknown,
    ClientApiV1MockInterviewVoucherFreeCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MockInterviewVoucherFreeCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MockInterviewVoucherFreeCreateResponse = ApiResponseUnit;

export type ClientApiV1MockInterviewVoucherFreeCreateVariables = unknown;

export const fetchClientApiV1MockInterviewVoucher = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MockInterviewVoucherResponse>(url, requestOptions);

export interface ClientApiV1MockInterviewVoucherQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MockInterviewVoucherResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MockInterviewVoucherQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MockInterviewVoucherQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MockInterviewVoucher(url, requestOptions),
  ...options,
});

export function useClientApiV1MockInterviewVoucherQuery(
  params: ClientApiV1MockInterviewVoucherQueryParams,
) {
  const result = useQuery<ClientApiV1MockInterviewVoucherResponse, unknown>(
    getClientApiV1MockInterviewVoucherQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/** 로그인한 사용자가 보유한 모의면접 이용권 목록을 id 내림차순으로 조회한다. isUsed 로 사용 여부를 필터링할 수 있다. */
export type ClientApiV1MockInterviewVoucherQueryString = Partial<{
  isUsed: boolean;
}>;

export type ClientApiV1MockInterviewVoucherResponse = ApiResponseGetMockInterviewVouchersResponse;

export interface ApiResponseUnit {
  statusCode:
    | '100 CONTINUE'
    | '101 SWITCHING_PROTOCOLS'
    | '102 PROCESSING'
    | '103 EARLY_HINTS'
    | '200 OK'
    | '201 CREATED'
    | '202 ACCEPTED'
    | '203 NON_AUTHORITATIVE_INFORMATION'
    | '204 NO_CONTENT'
    | '205 RESET_CONTENT'
    | '206 PARTIAL_CONTENT'
    | '207 MULTI_STATUS'
    | '208 ALREADY_REPORTED'
    | '226 IM_USED'
    | '300 MULTIPLE_CHOICES'
    | '301 MOVED_PERMANENTLY'
    | '302 FOUND'
    | '303 SEE_OTHER'
    | '304 NOT_MODIFIED'
    | '307 TEMPORARY_REDIRECT'
    | '308 PERMANENT_REDIRECT'
    | '400 BAD_REQUEST'
    | '401 UNAUTHORIZED'
    | '402 PAYMENT_REQUIRED'
    | '403 FORBIDDEN'
    | '404 NOT_FOUND'
    | '405 METHOD_NOT_ALLOWED'
    | '406 NOT_ACCEPTABLE'
    | '407 PROXY_AUTHENTICATION_REQUIRED'
    | '408 REQUEST_TIMEOUT'
    | '409 CONFLICT'
    | '410 GONE'
    | '411 LENGTH_REQUIRED'
    | '412 PRECONDITION_FAILED'
    | '413 CONTENT_TOO_LARGE'
    | '413 PAYLOAD_TOO_LARGE'
    | '414 URI_TOO_LONG'
    | '415 UNSUPPORTED_MEDIA_TYPE'
    | '416 REQUESTED_RANGE_NOT_SATISFIABLE'
    | '417 EXPECTATION_FAILED'
    | '418 I_AM_A_TEAPOT'
    | '421 MISDIRECTED_REQUEST'
    | '422 UNPROCESSABLE_CONTENT'
    | '422 UNPROCESSABLE_ENTITY'
    | '423 LOCKED'
    | '424 FAILED_DEPENDENCY'
    | '425 TOO_EARLY'
    | '426 UPGRADE_REQUIRED'
    | '428 PRECONDITION_REQUIRED'
    | '429 TOO_MANY_REQUESTS'
    | '431 REQUEST_HEADER_FIELDS_TOO_LARGE'
    | '451 UNAVAILABLE_FOR_LEGAL_REASONS'
    | '500 INTERNAL_SERVER_ERROR'
    | '501 NOT_IMPLEMENTED'
    | '502 BAD_GATEWAY'
    | '503 SERVICE_UNAVAILABLE'
    | '504 GATEWAY_TIMEOUT'
    | '505 HTTP_VERSION_NOT_SUPPORTED'
    | '506 VARIANT_ALSO_NEGOTIATES'
    | '507 INSUFFICIENT_STORAGE'
    | '508 LOOP_DETECTED'
    | '509 BANDWIDTH_LIMIT_EXCEEDED'
    | '510 NOT_EXTENDED'
    | '511 NETWORK_AUTHENTICATION_REQUIRED';
  message: string;
  errorCode?: string;
  data: unknown;
}

export interface CompleteUploadResponse {
  mockInterviewFileId: number;
  mockInterviewId: number;
  fileName: string;
  fileType: string;
  createdAtUTC: string;
  uploadedAtUTC?: string;
}

export interface DrawStudyChatUserPersonaShapeResponse {
  bodyType:
    | 'ROUND'
    | 'EGG'
    | 'WIDE'
    | 'MUSHROOM'
    | 'JELLY'
    | 'MICHELIN'
    | 'SQUARE'
    | 'TICKET'
    | 'BUG'
    | 'LEGO'
    | 'GHOST'
    | 'SNACK';
  patternType: 'NONE' | 'VERTICAL_STRIPE' | 'HORIZONTAL_STRIPE' | 'DOTS' | 'DIAG';
}

export interface CreateMockInterviewResponse {
  mockInterviewId: number;
  status:
    | 'INITIALIZED'
    | 'SUBMIT'
    | 'PREPARING'
    | 'READY'
    | 'ON_AIR'
    | 'FINISHED'
    | 'REPORT_GENERATED';
  type: 'BACKEND';
  interviewerPersonaType:
    | 'DEFAULT'
    | 'CURIOUS_LEAD'
    | 'CALM_ANALYST'
    | 'DIRECT_PRACTITIONER'
    | 'RELAXED_EXECUTIVE'
    | 'HYANGRO';
  link?: string;
  companyDescription?: string;
  resume?: MockInterviewFileResponse;
  portfolio?: MockInterviewFileResponse;
}

export interface MockInterviewFileResponse {
  id: number;
  fileName: string;
  fileType: string;
}

export interface CreateUploadUrlResponse {
  mockInterviewFileId: number;
  uploadUrl: string;
}

export interface Quiz {
  quizNumber: number;
  title: string;
  choices: QuizChoice[];
}

export interface QuizChoice {
  content: string;
}

export interface CreateStudyConversationResponse {
  conversationId: string;
}

export interface EvaluationChatResponse {
  answer: string;
  latencyMs: number;
}

export interface GetStudySummaryNotificationSettingResponse {
  id: number;
  isAgreed: boolean;
  agreedAtUTC?: string;
  updatedAtUTC?: string;
  createdAtUTC: string;
}

export interface GetStudyChatUserPersonaResponse {
  type: 'DEFAULT' | 'FRIENDLY' | 'GOLDEN_CHILD' | 'TSUNDERE' | 'MACHINE' | 'SOCRATES' | 'ROCKY';
  title: string;
  bodyType:
    | 'ROUND'
    | 'EGG'
    | 'WIDE'
    | 'MUSHROOM'
    | 'JELLY'
    | 'MICHELIN'
    | 'SQUARE'
    | 'TICKET'
    | 'BUG'
    | 'LEGO'
    | 'GHOST'
    | 'SNACK';
  patternType: 'NONE' | 'VERTICAL_STRIPE' | 'HORIZONTAL_STRIPE' | 'DOTS' | 'DIAG';
  earType: 'NONE' | 'CAT' | 'LEAF' | 'BUNNY' | 'ANTENNA' | 'BALL' | 'SPROUT';
  messageCount: number;
  studyLevel: 'LEVEL_1' | 'LEVEL_2';
}

export interface GetStudyChatUserPersonaTypeItemResponse {
  type: 'DEFAULT' | 'FRIENDLY' | 'GOLDEN_CHILD' | 'TSUNDERE' | 'MACHINE' | 'SOCRATES' | 'ROCKY';
  title: string;
  description: string;
}

export interface GetStudyChatUserPersonaTypeResponse {
  items: GetStudyChatUserPersonaTypeItemResponse[];
}

export interface GetStudyChatUserPersonaDetailItemResponse {
  type: 'DEFAULT' | 'FRIENDLY' | 'GOLDEN_CHILD' | 'TSUNDERE' | 'MACHINE' | 'SOCRATES' | 'ROCKY';
  studyLevel: 'LEVEL_1' | 'LEVEL_2';
  shapes: GetStudyChatUserPersonaDetailShapeResponse[];
}

export interface GetStudyChatUserPersonaDetailResponse {
  items: GetStudyChatUserPersonaDetailItemResponse[];
}

export interface GetStudyChatUserPersonaDetailShapeResponse {
  bodyType:
    | 'ROUND'
    | 'EGG'
    | 'WIDE'
    | 'MUSHROOM'
    | 'JELLY'
    | 'MICHELIN'
    | 'SQUARE'
    | 'TICKET'
    | 'BUG'
    | 'LEGO'
    | 'GHOST'
    | 'SNACK';
  patternType: 'NONE' | 'VERTICAL_STRIPE' | 'HORIZONTAL_STRIPE' | 'DOTS' | 'DIAG';
  earType: 'NONE' | 'CAT' | 'LEAF' | 'BUNNY' | 'ANTENNA' | 'BALL' | 'SPROUT';
  isSelected: boolean;
}

export interface GetStudyAgentWaitingListResponse {
  id: number;
  userId: number;
  updatedAtUTC?: string;
  createdAtUTC: string;
}

export interface GetMockInterviewDetailResponse {
  mockInterviewId: number;
  status:
    | 'INITIALIZED'
    | 'SUBMIT'
    | 'PREPARING'
    | 'READY'
    | 'ON_AIR'
    | 'FINISHED'
    | 'REPORT_GENERATED';
  type: 'BACKEND';
  link?: string;
  companyDescription?: string;
  estimatedDurationMinutes: number;
  interviewer: InterviewerResponse;
}

export interface InterviewerResponse {
  personaType:
    | 'DEFAULT'
    | 'CURIOUS_LEAD'
    | 'CALM_ANALYST'
    | 'DIRECT_PRACTITIONER'
    | 'RELAXED_EXECUTIVE'
    | 'HYANGRO';
  name: string;
  introduce: string;
}

export interface GetMockInterviewStatusResponse {
  status:
    | 'INITIALIZED'
    | 'SUBMIT'
    | 'PREPARING'
    | 'READY'
    | 'ON_AIR'
    | 'FINISHED'
    | 'REPORT_GENERATED';
  linkAnalyzedAtUTC?: string;
  isLinkAnalyzed: boolean;
  resumeAnalyzedAtUTC?: string;
  isResumeAnalyzed: boolean;
  portfolioAnalyzedAtUTC?: string;
  isPortfolioAnalyzed: boolean;
  questionGeneratedAtUTC?: string;
  isQuestionGenerated: boolean;
}

export interface GetNextMockInterviewQuestionResponse {
  hasNext: boolean;
  totalQuestionCount: number;
  question?: NextMockInterviewQuestionItem;
  parent?: ParentMockInterviewQuestionItem;
}

export interface NextMockInterviewQuestionItem {
  id: number;
  questionNumber: string;
  question: string;
  guideline: string;
}

export interface ParentMockInterviewQuestionItem {
  id: number;
  question: string;
  guideline: string;
  answer?: string;
  answeredAtUTC?: string;
}

export interface GetMockInterviewTypeItemResponse {
  code: string;
  displayName: string;
}

export interface GetMockInterviewTypesResponse {
  items: GetMockInterviewTypeItemResponse[];
}

export interface ApiResponseGetMockInterviewVouchersResponse {
  statusCode:
    | '100 CONTINUE'
    | '101 SWITCHING_PROTOCOLS'
    | '102 PROCESSING'
    | '103 EARLY_HINTS'
    | '200 OK'
    | '201 CREATED'
    | '202 ACCEPTED'
    | '203 NON_AUTHORITATIVE_INFORMATION'
    | '204 NO_CONTENT'
    | '205 RESET_CONTENT'
    | '206 PARTIAL_CONTENT'
    | '207 MULTI_STATUS'
    | '208 ALREADY_REPORTED'
    | '226 IM_USED'
    | '300 MULTIPLE_CHOICES'
    | '301 MOVED_PERMANENTLY'
    | '302 FOUND'
    | '303 SEE_OTHER'
    | '304 NOT_MODIFIED'
    | '307 TEMPORARY_REDIRECT'
    | '308 PERMANENT_REDIRECT'
    | '400 BAD_REQUEST'
    | '401 UNAUTHORIZED'
    | '402 PAYMENT_REQUIRED'
    | '403 FORBIDDEN'
    | '404 NOT_FOUND'
    | '405 METHOD_NOT_ALLOWED'
    | '406 NOT_ACCEPTABLE'
    | '407 PROXY_AUTHENTICATION_REQUIRED'
    | '408 REQUEST_TIMEOUT'
    | '409 CONFLICT'
    | '410 GONE'
    | '411 LENGTH_REQUIRED'
    | '412 PRECONDITION_FAILED'
    | '413 CONTENT_TOO_LARGE'
    | '413 PAYLOAD_TOO_LARGE'
    | '414 URI_TOO_LONG'
    | '415 UNSUPPORTED_MEDIA_TYPE'
    | '416 REQUESTED_RANGE_NOT_SATISFIABLE'
    | '417 EXPECTATION_FAILED'
    | '418 I_AM_A_TEAPOT'
    | '421 MISDIRECTED_REQUEST'
    | '422 UNPROCESSABLE_CONTENT'
    | '422 UNPROCESSABLE_ENTITY'
    | '423 LOCKED'
    | '424 FAILED_DEPENDENCY'
    | '425 TOO_EARLY'
    | '426 UPGRADE_REQUIRED'
    | '428 PRECONDITION_REQUIRED'
    | '429 TOO_MANY_REQUESTS'
    | '431 REQUEST_HEADER_FIELDS_TOO_LARGE'
    | '451 UNAVAILABLE_FOR_LEGAL_REASONS'
    | '500 INTERNAL_SERVER_ERROR'
    | '501 NOT_IMPLEMENTED'
    | '502 BAD_GATEWAY'
    | '503 SERVICE_UNAVAILABLE'
    | '504 GATEWAY_TIMEOUT'
    | '505 HTTP_VERSION_NOT_SUPPORTED'
    | '506 VARIANT_ALSO_NEGOTIATES'
    | '507 INSUFFICIENT_STORAGE'
    | '508 LOOP_DETECTED'
    | '509 BANDWIDTH_LIMIT_EXCEEDED'
    | '510 NOT_EXTENDED'
    | '511 NETWORK_AUTHENTICATION_REQUIRED';
  message: string;
  errorCode?: string;
  data: GetMockInterviewVouchersResponse;
}

export interface GetMockInterviewVoucherItemResponse {
  id: number;
  type: 'FREE';
  isUsed: boolean;
  usedAtUTC?: string;
  mockInterview?: MockInterviewResponse;
}

export interface GetMockInterviewVouchersResponse {
  items: GetMockInterviewVoucherItemResponse[];
}

export interface MockInterviewResponse {
  mockInterviewId: number;
  status:
    | 'INITIALIZED'
    | 'SUBMIT'
    | 'PREPARING'
    | 'READY'
    | 'ON_AIR'
    | 'FINISHED'
    | 'REPORT_GENERATED';
  type: 'BACKEND';
  interviewerPersonaType:
    | 'DEFAULT'
    | 'CURIOUS_LEAD'
    | 'CALM_ANALYST'
    | 'DIRECT_PRACTITIONER'
    | 'RELAXED_EXECUTIVE'
    | 'HYANGRO';
}

export interface GetMockInterviewReportResponse {
  mockInterview: MockInterviewSummaryResponse;
  questions: QuestionReportResponse[];
}

export interface MockInterviewSummaryResponse {
  type: 'BACKEND';
  durationSeconds: number;
  questionCount: number;
}

export interface QuestionReportResponse {
  questionNumber: string;
  question: string;
  answer?: string;
  bad?: string;
  good?: string;
  next?: string;
}

export interface GetMockInterviewQuestionItemResponse {
  id: number;
  question: string;
  guideline: string;
  isAnswered: boolean;
  answeredAtUTC?: string;
}

export interface GetMockInterviewQuestionsResponse {
  items: GetMockInterviewQuestionItemResponse[];
}

export interface GetMockInterviewPersonaItemResponse {
  code: string;
  displayName: string;
  introduce: string;
  tags: string[];
}

export interface GetMockInterviewPersonasResponse {
  items: GetMockInterviewPersonaItemResponse[];
}

export interface GetDownloadUrlResponse {
  downloadUrl: string;
}

export interface GetLectureChatRateLimitResponse {
  usedPercent: number;
  expireAtUTC: string;
}

export interface GetStudyMessageItemResponse {
  id: string;
  studyConversationId: string;
  parentMessageId?: string;
  messageKey?: string;
  type: 'USER' | 'ASSISTANT' | 'SYSTEM';
  content: string;
  createdAt: string;
}

export interface GetStudyMessageResponse {
  items: GetStudyMessageItemResponse[];
}

export interface GetLectureChatAvailabilityResponse {
  isAvailable: boolean;
  shouldShow: boolean;
  messageType: 'NO_MESSAGE' | 'WAITING_LIST' | 'NEED_VOUCHER' | 'LOGIN_REQUIRED';
  /**
   * 제거 필요
   */
  isB2bSubscription: boolean;
}

export interface GetStudyConversationItemResponse {
  id: string;
  title: string;
  lastMessagedAt?: string;
  createdAt: string;
}

export interface GetStudyConversationResponse {
  items: GetStudyConversationItemResponse[];
}

export interface GetStudyAgentUserSettingResponse {
  id: number;
  isProfileDataAllowed: boolean;
  isUserMemoryAllowed: boolean;
  isNextReplyRecommendAllowed: boolean;
  updatedAt?: string;
  createdAt: string;
}

export interface GetAgentUserMemoryResponse {
  content?: string;
  updatedAt?: string;
  createdAt: string;
}

export interface GetStudyChatDailyTokenUsagesResponse {
  items: Item[];
  summary: Summary;
}

export interface Item {
  id: number;
  aggregationDate: string;
  model: string;
  channel: 'LECTURE_ROOM';
  usageType:
    | 'STUDY_MESSAGE'
    | 'STUDY_NEXT_REPLY_RECOMMEND'
    | 'STUDY_CONVERSATION_SUMMARY'
    | 'STUDY_IMAGE_ANALYSIS'
    | 'STUDY_AGENT_USER_MEMORY';
  userType: 'B2C' | 'B2B' | 'B2B_SUBSCRIBER' | 'ADMIN';
  promptTokens: number;
  cachedPromptTokens: number;
  completionTokens: number;
  totalTokens: number;
  messageCount: number;
  estimatedInputCost: number;
  estimatedUncachedInputCost: number;
  estimatedCachedInputCost: number;
  estimatedOutputCost: number;
  estimatedTotalCost: number;
  createdAtUTC: string;
}

export interface Summary {
  totalPromptTokens: number;
  totalCachedPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  totalMessageCount: number;
  totalEstimatedInputCost: number;
  totalEstimatedUncachedInputCost: number;
  totalEstimatedCachedInputCost: number;
  totalEstimatedOutputCost: number;
  totalEstimatedTotalCost: number;
  averagePromptTokens: number;
  averageCachedPromptTokens: number;
  averageCompletionTokens: number;
  averageTotalTokens: number;
  averageMessageCount: number;
  averageEstimatedInputCost: number;
  averageEstimatedUncachedInputCost: number;
  averageEstimatedCachedInputCost: number;
  averageEstimatedOutputCost: number;
  averageEstimatedTotalCost: number;
}

export interface StudyConversationItemResponse {
  id: string;
  userId: number;
  courseId: number;
  title: string;
  lastMessagedAtUTC?: string;
  createdAtUTC: string;
}

export interface StudyConversationResponse {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: StudyConversationItemResponse[];
}

export interface StudyMessageItemResponse {
  id: string;
  studyConversationId: string;
  parentMessageId?: string;
  messageKey?: string;
  channel: 'LECTURE_ROOM';
  type: 'USER' | 'ASSISTANT' | 'SYSTEM';
  content: string;
  createdAtUTC: string;
}

export interface StudyMessageResponse {
  items: StudyMessageItemResponse[];
  totalCount: number;
}

/** HttpStatus */
export const enum HttpStatus {
  '100 CONTINUE' = '100 CONTINUE',
  '101 SWITCHING_PROTOCOLS' = '101 SWITCHING_PROTOCOLS',
  '102 PROCESSING' = '102 PROCESSING',
  '103 EARLY_HINTS' = '103 EARLY_HINTS',
  '200 OK' = '200 OK',
  '201 CREATED' = '201 CREATED',
  '202 ACCEPTED' = '202 ACCEPTED',
  '203 NON_AUTHORITATIVE_INFORMATION' = '203 NON_AUTHORITATIVE_INFORMATION',
  '204 NO_CONTENT' = '204 NO_CONTENT',
  '205 RESET_CONTENT' = '205 RESET_CONTENT',
  '206 PARTIAL_CONTENT' = '206 PARTIAL_CONTENT',
  '207 MULTI_STATUS' = '207 MULTI_STATUS',
  '208 ALREADY_REPORTED' = '208 ALREADY_REPORTED',
  '226 IM_USED' = '226 IM_USED',
  '300 MULTIPLE_CHOICES' = '300 MULTIPLE_CHOICES',
  '301 MOVED_PERMANENTLY' = '301 MOVED_PERMANENTLY',
  '302 FOUND' = '302 FOUND',
  '303 SEE_OTHER' = '303 SEE_OTHER',
  '304 NOT_MODIFIED' = '304 NOT_MODIFIED',
  '307 TEMPORARY_REDIRECT' = '307 TEMPORARY_REDIRECT',
  '308 PERMANENT_REDIRECT' = '308 PERMANENT_REDIRECT',
  '400 BAD_REQUEST' = '400 BAD_REQUEST',
  '401 UNAUTHORIZED' = '401 UNAUTHORIZED',
  '402 PAYMENT_REQUIRED' = '402 PAYMENT_REQUIRED',
  '403 FORBIDDEN' = '403 FORBIDDEN',
  '404 NOT_FOUND' = '404 NOT_FOUND',
  '405 METHOD_NOT_ALLOWED' = '405 METHOD_NOT_ALLOWED',
  '406 NOT_ACCEPTABLE' = '406 NOT_ACCEPTABLE',
  '407 PROXY_AUTHENTICATION_REQUIRED' = '407 PROXY_AUTHENTICATION_REQUIRED',
  '408 REQUEST_TIMEOUT' = '408 REQUEST_TIMEOUT',
  '409 CONFLICT' = '409 CONFLICT',
  '410 GONE' = '410 GONE',
  '411 LENGTH_REQUIRED' = '411 LENGTH_REQUIRED',
  '412 PRECONDITION_FAILED' = '412 PRECONDITION_FAILED',
  '413 CONTENT_TOO_LARGE' = '413 CONTENT_TOO_LARGE',
  '413 PAYLOAD_TOO_LARGE' = '413 PAYLOAD_TOO_LARGE',
  '414 URI_TOO_LONG' = '414 URI_TOO_LONG',
  '415 UNSUPPORTED_MEDIA_TYPE' = '415 UNSUPPORTED_MEDIA_TYPE',
  '416 REQUESTED_RANGE_NOT_SATISFIABLE' = '416 REQUESTED_RANGE_NOT_SATISFIABLE',
  '417 EXPECTATION_FAILED' = '417 EXPECTATION_FAILED',
  '418 I_AM_A_TEAPOT' = '418 I_AM_A_TEAPOT',
  '421 MISDIRECTED_REQUEST' = '421 MISDIRECTED_REQUEST',
  '422 UNPROCESSABLE_CONTENT' = '422 UNPROCESSABLE_CONTENT',
  '422 UNPROCESSABLE_ENTITY' = '422 UNPROCESSABLE_ENTITY',
  '423 LOCKED' = '423 LOCKED',
  '424 FAILED_DEPENDENCY' = '424 FAILED_DEPENDENCY',
  '425 TOO_EARLY' = '425 TOO_EARLY',
  '426 UPGRADE_REQUIRED' = '426 UPGRADE_REQUIRED',
  '428 PRECONDITION_REQUIRED' = '428 PRECONDITION_REQUIRED',
  '429 TOO_MANY_REQUESTS' = '429 TOO_MANY_REQUESTS',
  '431 REQUEST_HEADER_FIELDS_TOO_LARGE' = '431 REQUEST_HEADER_FIELDS_TOO_LARGE',
  '451 UNAVAILABLE_FOR_LEGAL_REASONS' = '451 UNAVAILABLE_FOR_LEGAL_REASONS',
  '500 INTERNAL_SERVER_ERROR' = '500 INTERNAL_SERVER_ERROR',
  '501 NOT_IMPLEMENTED' = '501 NOT_IMPLEMENTED',
  '502 BAD_GATEWAY' = '502 BAD_GATEWAY',
  '503 SERVICE_UNAVAILABLE' = '503 SERVICE_UNAVAILABLE',
  '504 GATEWAY_TIMEOUT' = '504 GATEWAY_TIMEOUT',
  '505 HTTP_VERSION_NOT_SUPPORTED' = '505 HTTP_VERSION_NOT_SUPPORTED',
  '506 VARIANT_ALSO_NEGOTIATES' = '506 VARIANT_ALSO_NEGOTIATES',
  '507 INSUFFICIENT_STORAGE' = '507 INSUFFICIENT_STORAGE',
  '508 LOOP_DETECTED' = '508 LOOP_DETECTED',
  '509 BANDWIDTH_LIMIT_EXCEEDED' = '509 BANDWIDTH_LIMIT_EXCEEDED',
  '510 NOT_EXTENDED' = '510 NOT_EXTENDED',
  '511 NETWORK_AUTHENTICATION_REQUIRED' = '511 NETWORK_AUTHENTICATION_REQUIRED',
}

/** PersonaBodyType */
export const enum PersonaBodyType {
  ROUND = 'ROUND',
  EGG = 'EGG',
  WIDE = 'WIDE',
  MUSHROOM = 'MUSHROOM',
  JELLY = 'JELLY',
  MICHELIN = 'MICHELIN',
  SQUARE = 'SQUARE',
  TICKET = 'TICKET',
  BUG = 'BUG',
  LEGO = 'LEGO',
  GHOST = 'GHOST',
  SNACK = 'SNACK',
}

/** PersonaPatternType */
export const enum PersonaPatternType {
  NONE = 'NONE',
  VERTICAL_STRIPE = 'VERTICAL_STRIPE',
  HORIZONTAL_STRIPE = 'HORIZONTAL_STRIPE',
  DOTS = 'DOTS',
  DIAG = 'DIAG',
}

/** MockInterviewStatus */
export const enum MockInterviewStatus {
  INITIALIZED = 'INITIALIZED',
  SUBMIT = 'SUBMIT',
  PREPARING = 'PREPARING',
  READY = 'READY',
  ON_AIR = 'ON_AIR',
  FINISHED = 'FINISHED',
  REPORT_GENERATED = 'REPORT_GENERATED',
}

/** MockInterviewType */
export const enum MockInterviewType {
  BACKEND = 'BACKEND',
}

/** InterviewerPersonaType */
export const enum InterviewerPersonaType {
  DEFAULT = 'DEFAULT',
  CURIOUS_LEAD = 'CURIOUS_LEAD',
  CALM_ANALYST = 'CALM_ANALYST',
  DIRECT_PRACTITIONER = 'DIRECT_PRACTITIONER',
  RELAXED_EXECUTIVE = 'RELAXED_EXECUTIVE',
  HYANGRO = 'HYANGRO',
}

/** StudyChatUserPersonaType */
export const enum StudyChatUserPersonaType {
  DEFAULT = 'DEFAULT',
  FRIENDLY = 'FRIENDLY',
  GOLDEN_CHILD = 'GOLDEN_CHILD',
  TSUNDERE = 'TSUNDERE',
  MACHINE = 'MACHINE',
  SOCRATES = 'SOCRATES',
  ROCKY = 'ROCKY',
}

/** PersonaEarType */
export const enum PersonaEarType {
  NONE = 'NONE',
  CAT = 'CAT',
  LEAF = 'LEAF',
  BUNNY = 'BUNNY',
  ANTENNA = 'ANTENNA',
  BALL = 'BALL',
  SPROUT = 'SPROUT',
}

/** StudyLevel */
export const enum StudyLevel {
  LEVEL_1 = 'LEVEL_1',
  LEVEL_2 = 'LEVEL_2',
}

/** MockInterviewVoucherType */
export const enum MockInterviewVoucherType {
  FREE = 'FREE',
}

/** MessageType */
export const enum MessageType {
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
  SYSTEM = 'SYSTEM',
}

/** LectureChatAvailabilityMessageType */
export const enum LectureChatAvailabilityMessageType {
  NO_MESSAGE = 'NO_MESSAGE',
  WAITING_LIST = 'WAITING_LIST',
  NEED_VOUCHER = 'NEED_VOUCHER',
  LOGIN_REQUIRED = 'LOGIN_REQUIRED',
}

/** Channel */
export const enum Channel {
  LECTURE_ROOM = 'LECTURE_ROOM',
}

/** StudyChatDailyTokenUsageType */
export const enum StudyChatDailyTokenUsageType {
  STUDY_MESSAGE = 'STUDY_MESSAGE',
  STUDY_NEXT_REPLY_RECOMMEND = 'STUDY_NEXT_REPLY_RECOMMEND',
  STUDY_CONVERSATION_SUMMARY = 'STUDY_CONVERSATION_SUMMARY',
  STUDY_IMAGE_ANALYSIS = 'STUDY_IMAGE_ANALYSIS',
  STUDY_AGENT_USER_MEMORY = 'STUDY_AGENT_USER_MEMORY',
}

/** UserType */
export const enum UserType {
  B2C = 'B2C',
  B2B = 'B2B',
  B2B_SUBSCRIBER = 'B2B_SUBSCRIBER',
  ADMIN = 'ADMIN',
}
